import React from 'react';
import PropTypes from 'prop-types';
import createDate from './formatter-date';
import createTime from './formatter-hour';
import getElementType from '../Utils/getElementType';

const FormatterTypes = {
  number: 'number',
  currency: 'currency',
  date: 'date',
  dateDetailed: 'date-detailed',
  hour: 'hour',
  dateTime: 'date-time',
};

// #### normalizza
// Dato un valore in ingresso come stringa lo normalizza rimuovendo i separatori
// di migliaia
const normalizza = (value, defaultValue) => {
  const absValue = Math.abs(value);
  return isNaN(absValue) ? defaultValue : absValue;
};

// #### toFixed
// Implementazione alternativa del toFixed() javascript che tratta i float come decimali
// evitando i classici problemi di arrotondamento come (0.615).toFixed(2)=0.61)
const toFixed = (valore, precisione) => {
  const precisioneNorm = normalizza(precisione, 2);
  const power = 10 ** precisioneNorm;
  // moltiplica per la precisione, arrotonda correttamente e poi dividi
  // di nuovo per arrotondare correttamente
  return (Math.round(normalizza(valore) * power) / power).toFixed(precisioneNorm);
};

// #### parse
// Esegue il parse di un numero sotto forma di stringa restituendo il numero rappresentato
const parse = (valoreP, separatoreP) => {
  // se non specifico valore restituisco 0
  const valore = valoreP || 0;

  // se è un numero lo restituisco
  if (typeof valore === 'number') { return valore; }

  // Se non specifico un separatore uso quello di default in Italia
  const separatore = separatoreP || ',';

  // Elimino tutti i caratteri tranne cifre, segni meno e separatori decimali
  const regex = new RegExp(`[^0-9-${separatore}]`, ['g']);

  const unformatted = parseFloat(
    (`${valore}`)
      .replace(/\((.*)\)/, '-$1')
      .replace(regex, '')
      .replace(separatore, '.'),
  );

  // ritorno il valore
  return !isNaN(unformatted) ? unformatted : 0;
};

// #### format
// Fromatta il valore in ingresso specificando precisione e separatori da usare
const formattaNumero = (numeroP, precisioneP, separatoreMigliaia, seperatoreDecimali) => {
  if (numeroP === null) { return ''; }

  // leggo il numero
  const numero = parse(numeroP);

  // leggo le opzioni passate
  const opts = {
    precisione: precisioneP,
    migliaia: separatoreMigliaia,
    decimali: seperatoreDecimali,
  };

  // normalizza la precisione
  const usePrecision = normalizza(opts.precisione);

  // eseguo i calcoli
  const negative = numero < 0 ? '-' : '';

  const base = `${parseInt(toFixed(Math.abs(numero || 0), usePrecision), 10)}`;

  const mod = base.length > 3 ? base.length % 3 : 0;

  // formatto il numero
  return negative + (mod ? base.substr(0, mod) + opts.migliaia : '')
    + base.substr(mod).replace(/(\d{3})(?=\d)/g, `$1${opts.migliaia}`)
    + (usePrecision ? opts.decimali + toFixed(Math.abs(numero), usePrecision).split('.')[1] : '');
};

const format = (value, type, decimali) => {
  if (value == null || type == null) {
    return null;
  }
  switch (type) {
    case FormatterTypes.number: // 123.456
      return formattaNumero(value, decimali || 0, '.', ',');
    case FormatterTypes.currency: // 123.456,000
      return formattaNumero(value, decimali || 2, '.', ',');
    case FormatterTypes.date:
      return createDate(value, type);
    case FormatterTypes.dateDetailed:
      return createDate(value, type);
    case FormatterTypes.hour:
      return createTime(value, type);
    case FormatterTypes.dateTime:
      return createDate(value, type);
    default:
      throw new TypeError('Formatter: formato sconosciuto');
  }
};

/**
Il componente Formatter è stato ideato per permettere di formattare alcuni tipi di dato (numeri, date, cifre)
con semplicità.
 */
const Formatter = ({
  className, valore, tipo, decimali, as,
}) => {
  const ElementType = getElementType(Formatter, { as });
  return (
    <ElementType className={className}>
      {format(valore, tipo, decimali)}
    </ElementType>
  );
};

Formatter.propTypes = {
  /** Definisce il tag con il quale renderizzare il componente */
  as: PropTypes.string,
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Il numero di decimali da mostrare */
  decimali: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /** Il tipo di formattazione */
  tipo: PropTypes.oneOf([
    'number',
    'currency',
    'date',
    'date-detailed',
    'hour',
    'date-time']).isRequired,
  /** Il valore da formattare */
  valore: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
};

Formatter.defaultProps = {
  as: 'span',
  className: '',
  decimali: 0,
  valore: null,
};

export default Formatter;
